// @ts-strict-ignore
import type {Segmentation} from 'common/types/reports/query';
import type {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';

// TODO: @opslead - fold these types and possibly other duplicates in this file into iron/common/report/constants.ts
export enum UserRole {
  Admin = `admin`,
  Analyst = `analyst`,
  BillingAdmin = `billing admin`,
  Consumer = `consumer`,
  /** only used as org role, not as a project role */
  Member = `member`,
  Messenger = `messenger`,
  Owner = `owner`,
}

export enum EntityType {
  Report = `report`,
  Dashboard = `dashboard`,
  Cohort = `cohort`,
  CustomEvent = `customevent`,
  CustomProperty = `custom property`,
  Event = `event`,
  Property = `property`,
  LookupTable = `lookup table`,
  DataGroup = `data group`,
  Journey = `journey`,
  Workspace = `workspace`,
  Metric = `metric`,
  Behavior = `behavior`,
  Canvas = `canvas`,
  Experiment = `experiment`,
}

/**
 * These are specialization of EntityType so that they can be specially handled. They will be saved/shared based
 * on the EntityType they are associated with.
 */
export enum SubEntityType {
  // a special type of custom property
  BorrowedProperty = `borrowed property`,
}

/* eslint-disable @typescript-eslint/naming-convention */
export enum EntityTypeKey {
  report = `Report`,
  dashboard = `Dashboard`,
  cohort = `Cohort`,
  customevent = `CustomEvent`,
  event = `Event`,
  property = `property`,
  'custom property' = `CustomProperty`,
  'lookup table' = `LookupTable`,
  'data group' = `DataGroup`,
  journey = `Journey`,
  workspace = `Workspace`,
  metric = `Metric`,
  behavior = `Behavior`,
}
/* eslint-enable */

// labels for displaying entity type in copy (non-plural)
export enum EntityTypeNameSingular {
  Report = `report`,
  Dashboard = `board`,
  Cohort = `cohort`,
  CustomEvent = `custom event`,
  CustomProperty = `custom property`,
  BorrowedProperty = `borrowed property`,
  EventProperty = `event property`,
  UserProperty = `user property`,
  LookupTable = `lookup table`,
  Event = `event`,
  Property = `property`,
  DataGroup = `data group`,
  Journey = `journey`,
  Workspace = `data view`,
  Metric = `formula`,
  Behavior = `behavior`,
}

// labels for displaying entity type in copy (plural)
export enum EntityTypeNamePlural {
  Report = `reports`,
  Dashboard = `boards`,
  Cohort = `cohorts`,
  CustomEvent = `custom events`,
  CustomProperty = `custom properties`,
  BorrowedProperty = `borrowed properties`,
  Event = `events`,
  Property = `properties`,
  LookupTable = `lookup tables`,
  DataGroup = `data groups`,
  Journey = `journeys`,
  Workspace = `data views`,
  Metric = `formulas`,
  Behavior = `behaviors`,
}

export enum SharableEntityType {
  Report = `bookmarks`,
  Dashboard = `dashboards`,
  Cohort = `cohorts`,
  CustomEvent = `custom-events`,
  CustomProperty = `custom-properties`,
  LookupTable = `lookup-tables`,
  Metric = `metric`,
  Behavior = `behavior`,
  Theme = `theme`,
  Canvas = `canvases`,
  Experiment = `experiments`,
  Profile = `profile`,
}

export interface SharableEntityGrant {
  owner?: UserShareGrant;
  userShares: UserShareGrant[];
  teamShares: TeamShareGrant[];
  projectShares: ProjectShareGrant[];
  name: string;
  link: string;
}

export enum GrantStatus {
  New = `new`,
  Updated = `updated`,
  Existing = `existing`,
}

export type ProjectShareGrant = {id: number; canEdit: boolean};
export type TeamShareGrant = {id: number; name: string; canEdit: boolean};
export type UserShareGrant = {id: number; email: string; name?: string; canEdit: boolean};

export enum OverrideModal {
  Copy = `copy`,
  Save = `save`,
  Closed = `closed`,
  Delete = `delete`,
  SubsetOfItems = `subset`,
}

export interface EntityTypePermissions {
  role: UserRole;
  can_update_basic: boolean;
  can_share: boolean;
}

export type BaseEntityId = number | string;

interface BaseEntity {
  id?: BaseEntityId;
  description?: string;
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean; // is the user an owner with the ability to override restricted reports for editing and sharing
  allow_staff_override?: boolean; // is the user a staff user without project permissions
}

export interface Entity extends BaseEntity {
  name: string;

  created?: string;
  userId?: number;
  userName?: string;
  userEmail?: string;

  modified?: string;
  lastModifiedUserId?: number;
  lastModifiedUserName?: string;
  lastModifiedUserEmail?: string;
}
export type DashboardCardId = string | number;

export interface PublicDashboard extends BaseEntity {
  id: number;
  title: string;
  description: null | string;
  card_order: number[];
  // cards are not required when getting list of public dashboards
  cards?: Obj[];
  time_filter: Obj;
  filters: Obj[];
  last_modified?: any;

  creator_id: null | number; // null creatorId means it was a legacy auto-generated dashboard with no human creator
  creator_name: null | string; // full name of creator
}

/** alternatives is the list of events that are in a custom event */
export interface CustomEventAlternative {
  event: string;
  serialized?: string;
  valid_segfilter?: ValidSegfilter;
}

export interface ValidSegfilter {
  filters: Segfilter[] | Segmentation[];
  op: string;
}

export interface CustomEventDefinition extends BaseEntity {
  alternatives: CustomEventAlternative[];
  deleted: number;
  id: number;
  isCollectEverything?: boolean;
  is_collect_everything_event: boolean;
  name: string;
  project_id: number;
  value?: string;
}

// re-export so entity types are collected under saved-entities/types
export type {CustomProperty} from 'common/types/reports/query';

export const GlobalAccessParamName = `global_access_type`;
export const SaveBeforeSharingModalTitle = `Save Before Sharing`;

export const enum OwnerFilters {
  All = `Created By Anyone`,
  Yours = `Created By You`,
  NotYours = `Not Created By You`,
  Shared = `Shared With You`,
}
